import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ErrorNotify from "../../components/ErrorNotify/ErrorNotify";
import HomeAvatars from "../../components/HomeAvatars/HomeAvatars";
import VideoContainer from "../../components/MainVideo/MainVideo";
import MainModal from "../../components/Modal/Modal";
import Spinner from "../../components/Spinner/Spinner";
import { changeFormData, resetFormData, setPlatformScenes, setVoicesList } from "../../redux/slices";
import { setPlatformAvatars } from "../../redux/slices/platformAvatarsSlice";
import { listPlatformAvatars, listPlatformScenes, listVoices } from "../../utils/api";
import styles from "./Home.module.scss";
import { LocalStorageKey, Scene, translateTemper, translateUseCase, useLocalStorage, useScrollTo, VoiceTypes } from "../../utils";
import { IRootState } from "../../redux/store";
import { TAvatar } from "@common";
import logo from "../../assets/pictures/visper-logo-scene.svg";
import { scenesId } from "./constants";
import { useMediaPreview } from "../../components/PreviewAvatarMedia/MediaPreviewProvider";
import { setAppSessionStart } from "../../utils/cookieUtils";

const HomePage = () => {
	const localStorageKeyPlatformAvatars = LocalStorageKey.PlatformAvatars;
	const localStorageKeyVoices = LocalStorageKey.PlatformVoices;
	const localStorageKeyScenes = LocalStorageKey.PlatformScenes;
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isError, setIsError] = useState<boolean>(false);
	const [modal, setModal] = useState<"error" | "none">("none");
	const avatarsSectionRef = useRef<HTMLDivElement | null>(null);
	const avatars: TAvatar[] | [] = useSelector((state: IRootState) => state.platformAvatars);
	const availableVoices: VoiceTypes[] | [] = useSelector((state: IRootState) => state.voicesList);
	const sceneList: Scene[] | [] = useSelector((state: IRootState) => state.platformScenes);
	const { setValue: setVoices, getValue: getVoices } = useLocalStorage(localStorageKeyVoices);
	const { setValue: setScenes, getValue: getScenes } = useLocalStorage(localStorageKeyScenes);
	const { setValue, getValue } = useLocalStorage(localStorageKeyPlatformAvatars);
	const { setCurrentTab } = useMediaPreview();
	const dispatch = useDispatch();
	const scrollToAvatars = useScrollTo(avatarsSectionRef);

		const fetchVoices = useCallback(() => {
			return listVoices()
				.then((res) => res.json())
				.then((json) => {
					const voices = json.voices.map(
						({ voice_id, name, preview_url, labels }: Record<string, any>) => ({
							voice_id,
							name,
							preview_url,
							gender: labels.gender,
							useCase: translateUseCase(labels.use_case),
							temper: translateTemper(labels.description),
						}),
					);
					dispatch(setVoicesList(voices));
					setVoices(voices);
				});
		}, []);
	
		const fetchPlatformScenes = useCallback(() => {
			return listPlatformScenes()
				.then((res) => res.json())
				.then((json) => {
					const scenes = json
					.filter((el: Scene) => scenesId.includes(el.id))
					.map((el: Scene) => ({
						value: el.id,
						description: el.name,
					}));
					dispatch(setPlatformScenes(scenes));
					setScenes(scenes);
				});
		}, []);

	const fetchPlatformAvatars = useCallback(() => {
		return listPlatformAvatars()
			.then((res) => res.json())
			.then((json) => {
				dispatch(setPlatformAvatars(json));
				setValue(json);
			});
	}, []);

	const fetchAllData = useCallback(() => {
		setIsError(false);
		setIsLoading(true);
		Promise.all([fetchVoices(), fetchPlatformScenes(), fetchPlatformAvatars()])
			.catch(() => setIsError(true))
			.finally(() => setIsLoading(false));
	}, [fetchVoices, fetchPlatformScenes, fetchPlatformAvatars]);

	useEffect(() => {
		dispatch(resetFormData());
		dispatch(changeFormData({ logo }));
		if (avatars.length && availableVoices.length && sceneList.length) return;

		const storedAvatars: TAvatar[] | [] = getValue([]);
		const storedVoices: VoiceTypes[] = getVoices([]);
		const storedScenes: Scene[] = getScenes([]);

		if (storedVoices.length && storedScenes.length && storedAvatars.length) {
			dispatch(setVoicesList(storedVoices));
			dispatch(setPlatformScenes(storedScenes));
			dispatch(setPlatformAvatars(storedAvatars));
		} else {
			fetchAllData();
		}

		setCurrentTab("appearance");
		setAppSessionStart();
	}, []);

	useEffect(() => {
		if (isError) {
			setModal("error");
		}
	}, [isError]);

	const closeModal = () => {
		setModal("none");
		fetchPlatformAvatars();
	};

	const renderModal = () => {
		if (modal === "none") return null;

		const modalComponents = {
			error: <ErrorNotify onClose={closeModal} />,
		};

		return <MainModal>{modalComponents[modal]}</MainModal>;
	};

	if (isLoading) {
		return (
			<div className={styles.spinnerWrapper}>
				<Spinner size={"medium"} />
			</div>
		);
	}

	return (
		<div className={styles.container}>
			{modal !== "none" && renderModal()}
			<div className={styles.heroSection}>
				<div className={styles.heroTitleContainer}>
					<h1 className={`${styles.title} ${styles.titleHighlight}`}>Visper — больше, чем видео.</h1>
					<h1 className={styles.title}>Теперь это живые цифровые сотрудники.</h1>
				</div>
				<p>Замените безликий чат-бот на интерактивного аватара, который смотрит<br /> в глаза вашим клиентам, говорит вашим голосом и чувствует эмоции.<br /> Больше вовлечённости, больше доверия, больше продаж.</p>
				<button type="button" className={styles.primaryButton} onClick={scrollToAvatars}>Создать аватара бесплатно</button>
			</div>
			<VideoContainer />
			<div ref={avatarsSectionRef} className={styles.avatarsSection}>
				<HomeAvatars />
			</div>
		</div>
	);
};

export default HomePage;
